import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminIntensityLevelKeys } from "@/lib/query";

export interface AdminIntensityLevel {
  id: string;
  level: number;
  label: string;
  description: string | null;
  color: string;
}

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export function useAdminIntensityLevels() {
  return useQuery({
    queryKey: adminIntensityLevelKeys.list(),
    queryFn: async (): Promise<AdminIntensityLevel[]> => {
      const res = await fetch("/api/admin/intensity-levels");
      const data = await readJsonOrThrow<{ levels: AdminIntensityLevel[] }>(res);
      return data.levels;
    },
  });
}

export function useUpdateIntensityLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      label?: string;
      description?: string | null;
      color?: string;
    }) => {
      const { id, ...patch } = input;
      const res = await fetch(`/api/admin/intensity-levels/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      return readJsonOrThrow<AdminIntensityLevel>(res);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminIntensityLevelKeys.all });
      toast.success("Intensity level updated");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to update intensity level");
    },
  });
}
